import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import { process } from "@progress/kendo-data-query";
import "@progress/kendo-theme-default/dist/all.css";
import "./tasks.scss";
import "./tasks.css";
import {
  GetDynamicTableJson,
  PutDynamicTableJson,
} from "../../../../services/api/task-api";
import { FluentButton } from "../../../FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../assets/fluent-ui-icon";
import _ from "lodash";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import PropTypes from "prop-types";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { formatDateTimeWithSeconds } from "../../../../util/time-helpers";
import { TaskObjectViewer } from "./task-object-viewer";

const useInvestigatorEditable = (gridDef, isView) =>
  useMemo(() => {
    const col = gridDef?.find(
      (c) => c.title === "Tag Transactions (Investigator)"
    );
    return col && col.editable === "true" && !isView;
  }, [gridDef, isView]);

const TableCellCheckbox = (props) => {
  const handleChange = (e) => {
    const updatedItem = {
      ...props.dataItem,
      [props.field === "['Tag Transactions (Compliance Mgr.)']"
        ? "Tag Transactions (Compliance Mgr.)"
        : props.field]: e.target.checked.toString(),
    };

    props.onChange({
      dataItem: updatedItem,
      field:
        props.field === "['Tag Transactions (Compliance Mgr.)']"
          ? "Tag Transactions (Compliance Mgr.)"
          : props.field,
      value: e.target.checked.toString(),
      syntheticEvent: e,
    });
  };

  const isChecked = () => {
    if (props.field === "['Tag Transactions (Compliance Mgr.)']") {
      return (
        props.dataItem["Tag Transactions (Compliance Mgr.)"] === "true" ||
        props.dataItem["Tag Transactions (Compliance Mgr.)"] === true
      );
    } else {
      return (
        props.dataItem[props.field] === "true" ||
        props.dataItem[props.field] === true
      );
    }
  };

  return (
    <td {...props.tdProps}>
      <input
        disabled={props.editable === "true" && !props.IsView ? false : true}
        type="checkbox"
        checked={isChecked()}
        onChange={handleChange}
      />
    </td>
  );
};

const TableCellButton = (props) => {
  const [jsonObjectViewerOpen, setJsonObjectViewerOpen] = useState(false);

  return (
    <>
      {jsonObjectViewerOpen && (
        <TaskObjectViewer
          open={jsonObjectViewerOpen}
          ColumnName={props.title}
          Value={props?.dataItem?.[props.title]}
          OnCancel={() => setJsonObjectViewerOpen(false)}
        />
      )}
      <td colSpan="1" className="k-table-td" role="gridcell">
        <button
          disabled={props.editable !== "true"}
          className="td-object-viewer"
          onClick={() => setJsonObjectViewerOpen(true)}
          type="button"
        >
          View Details
        </button>
      </td>
    </>
  );
};

const TelerikDataGrid = React.memo((props) => {
  const _export = useRef(null);
  const [data, setData] = useState([]);
  const [dataState, setDataState] = useState({
    skip: 0,
    take: 20,
    sort: [],
    filter: [],
  });
  const [dataResult, setDataResult] = useState(process([], dataState));
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [lockedColumn, setLockedColumn] = useState(null);

  // build dropdown options
  const freezeOptions = useMemo(() => {
    const cols =
      props?.jsonGridDefinition
        ?.filter((c) => c.hidden === "false")
        .map((c) => ({
          text: c.title,
          value:
            c.field === "Tag Transactions (Compliance Mgr.)"
              ? "['Tag Transactions (Compliance Mgr.)']"
              : c.field,
        })) || [];
    return [{ text: "Select Column to Lock", value: null }, ...cols];
  }, [props.jsonGridDefinition]);

  // load persisted single locked column
  useEffect(() => {
    const key = `grid_locked_col_${props.jsonId}`;
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setLockedColumn(parsed);
      } catch (e) {
        console.warn(
          `[TelerikDataGrid] Failed to parse locked column state for key "${key}"`,
          e
        );
        localStorage.removeItem(key);
      }
    }
  }, [props.jsonId]);

  const persistLocked = (val) => {
    const key = `grid_locked_col_${props.jsonId}`;
    localStorage.setItem(key, JSON.stringify(val));
  };

  const handleFreezeChange = (e) => {
    const val = e.value?.value ?? null;
    setLockedColumn(val);
    persistLocked(val);
  };

  // if definition changes and selected column no longer exists -> reset
  useEffect(() => {
    if (
      lockedColumn !== null &&
      !freezeOptions.some((o) => o.value === lockedColumn)
    ) {
      setLockedColumn(null);
      persistLocked(null);
    }
  }, [freezeOptions, lockedColumn]);

  const handleDataStateChange = (event) => {
    setDataState(event.dataState);
    setDataResult(process(data, event.dataState));
  };

  useEffect(() => {
    GetGridData();
  }, []);

  useEffect(() => {
    if (isFirstRender && data.length > 0) {
      setIsFirstRender(false);
      return;
    }

    if (data.length > 0) {
      HandleSave();
    }
  }, [data]);

  const GetGridData = () => {
    let modifiedDataState = { ...dataState };
    modifiedDataState.filter = JSON.parse(props.jsonFilter);

    GetDynamicTableJson(props.jsonId).then((response) => {
      var data = JSON.parse(response.data.JsonData).map((item) => ({
        ...item,
        inEdit: props.jsonDataNoEdit === true ? false : true,
      }));
      setData(data);
      setDataState(modifiedDataState);
      setDataResult(process(data, modifiedDataState));
    });
  };

  const ItemChange = useCallback(
    (event) => {
      const updatedData = data.map((item) =>
        item.Id === event.dataItem.Id
          ? { ...item, [event.field]: event.value }
          : item
      );
      setData(updatedData);
      setDataResult(process(updatedData, dataState));
    },
    [data, dataState]
  );

  const SaveData = () => {
    const updatedData = {
      Id: props.jsonId,
      JsonData: JSON.stringify(data),
      GridDefinition: JSON.stringify(props.jsonGridDefinition),
    };

    if (props.jsonFilter !== "") {
      PutDynamicTableJson(props.jsonId, updatedData).then((response) => {
        console.log("Data updated successfully");
      });
    }
  };

  const HandleSave = _.debounce(SaveData, 1000);

  const excelExport = async () => {
    if (_export.current !== null) {
      let exportData = { data: data, total: data.length };

      if (dataState.filter) {
        let dState = { ...dataState };
        dState.take = dataResult.data.length;
        exportData = process(data, dState);
      }

      exportData?.data?.forEach((item) => {
        let cVal = item["Tag Transactions (Compliance Mgr.)"];
        let IVal = item["Tag Transactions (Investigator)"];

        if (cVal === "") item["Tag Transactions (Compliance Mgr.)"] = "false";
        if (IVal === "") item["Tag Transactions (Investigator)"] = "false";
      });

      await _export.current.save(exportData);
    }
  };

  const investigatorEditable = useInvestigatorEditable(
    props.jsonGridDefinition,
    props.IsView
  );

  const selectAll = useCallback(() => {
    const allRows = getAllFilteredRows();
    const updatedData = data.map((item) =>
      allRows.some((row) => row.Id === item.Id)
        ? { ...item, ["Tag Transactions (Investigator)"]: "true" }
        : item
    );
    setData(updatedData);
    setDataResult(process(updatedData, dataState));
  }, [data, dataState]);

  const unselectAll = useCallback(() => {
    const allRows = getAllFilteredRows();
    const updatedData = data.map((item) =>
      allRows.some((row) => row.Id === item.Id)
        ? { ...item, ["Tag Transactions (Investigator)"]: "false" }
        : item
    );
    setData(updatedData);
    setDataResult(process(updatedData, dataState));
  }, [data, dataState]);

  const getAllFilteredRows = () => {
    const dState = {
      ...dataState,
      skip: 0,
      take: data.length,
    };
    return process(data, dState).data;
  };

  return (
    <div className={isFullScreen ? "fullscreen-grid jsonTable" : "jsonTable"}>
      <div className="actions">
        {freezeOptions.length > 1 && (
          <div className="freeze-dropdown">
            <DropDownList
              data={freezeOptions}
              textField="text"
              dataItemKey="value"
              value={
                freezeOptions.find((o) => o.value === lockedColumn) ||
                freezeOptions[0]
              }
              onChange={handleFreezeChange}
              style={{ width: 220 }}
            />
          </div>
        )}
        {investigatorEditable && (<>
          <FluentButton text="Tag All Transactions" onClick={selectAll} aria-label="Tag all filtered transactions" />{" "}
          <FluentButton text="Untag All Transactions" onClick={unselectAll} />
        </>

        )}
        <FluentButton text="Export to XLS" onClick={excelExport} />
        <FluentButton
          icon={
            <FluentUiIcon
              name={
                isFullScreen
                  ? "FullScreenMinimizeFilled"
                  : "FullScreenMaximizeFilled"
              }
              color={"#0F6CBD"}
              size={20}
            />
          }
          onClick={() => {
            setIsFullScreen((fc) => !fc);
          }}
        />
      </div>
      {props?.jsonGridDefinition?.length > 0 && data.length > 0 && (
        <>
          <ExcelExport
            ref={_export}
            fileName={`Detailed Transaction Analysis-${formatDateTimeWithSeconds(
              new Date()
            )}.xlsx`}
          >
            {props?.jsonGridDefinition?.map((column) => {
              return (
                column.hidden === "false" && (
                  <ExcelExportColumn
                    key={column?.id}
                    field={
                      column.field === "Tag Transactions (Compliance Mgr.)"
                        ? "['Tag Transactions (Compliance Mgr.)']"
                        : column.field
                    }
                    title={column?.title}
                  />
                )
              );
            })}
          </ExcelExport>
          <Grid
            data={dataResult.data}
            pageable={{ buttonCount: 5, pageSizes: [5, 10, 20, 50, 100] }}
            sortable
            filterable={true}
            {...dataState}
            onDataStateChange={handleDataStateChange}
            editField="inEdit"
            onItemChange={ItemChange}
            total={dataResult.total}
          >
            {props.jsonGridDefinition.map(
              (column) =>
                column.hidden === "false" && (() => {
                  const fieldNormalized =
                    column.field === "Tag Transactions (Compliance Mgr.)"
                      ? "['Tag Transactions (Compliance Mgr.)']"
                      : column.field;
                  const isLocked = lockedColumn === fieldNormalized;
                  return (
                    <GridColumn
                      key={column.id}
                      locked={isLocked}
                      width={
                        isLocked
                          ? (column.width ? Number(column.width) : 180)
                          : (column.width ? Number(column.width) : undefined)
                      }
                      editable={
                        column.editable === "true" && !props.IsView ? true : false
                      }
                      filterable={
                        column.field !== "Tag Transactions (Compliance Mgr.)" ||
                        !props.jsonDataNoEdit
                      }
                      field={fieldNormalized}
                      title={column.title}
                      editor={
                        column.optionList?.length === 0
                          ? column.editor
                          : undefined
                      }
                      cell={
                        column.type === "Checkbox"
                          ? (cellProps) => (
                            <TableCellCheckbox
                              {...cellProps}
                              editable={column.editable}
                              IsView={props.IsView}
                            />
                          )
                          : column.type === "JSONObjectViewer"
                            ? (cellProps) => (
                              <TableCellButton
                                {...cellProps}
                                title={column.title}
                                editable={column.editable}
                              />
                            )
                            : undefined
                      }
                    />
                  );
                })()
            )}
          </Grid>
        </>
      )}
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.jsonId === nextProps.jsonId &&
    prevProps.jsonFilter === nextProps.jsonFilter &&
    prevProps.jsonDataNoEdit === nextProps.jsonDataNoEdit &&
    prevProps.IsView === nextProps.IsView &&
    JSON.stringify(prevProps.jsonGridDefinition) === JSON.stringify(nextProps.jsonGridDefinition)
  );
});

export default TelerikDataGrid;
TelerikDataGrid.propTypes = {
  jsonId: PropTypes.number,
  jsonFilter: PropTypes.string,
  jsonDataNoEdit: PropTypes.bool,
  jsonGridDefinition: PropTypes.array,
  IsView: PropTypes.bool,
};